import { useState } from 'react';
import { UserService } from '@/api/services/userService';
import {
  QrCodeResult,
  QrCodeVerifyResult
} from '@/api/types';

/**
 * 二维码管理相关的 hook
 * 处理二维码获取、验证、刷新等操作
 */
export function useUserQR() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentQrCode, setCurrentQrCode] = useState<QrCodeResult | null>(null);
  const [verifyPolling, setVerifyPolling] = useState(false);

  /**
   * 获取二维码
   * @param type 二维码类型（可选）
   * @returns Promise<QrCodeResult | null> 二维码信息
   */
  const getQrCode = async (type?: 'user_profile' | 'group_invite'): Promise<QrCodeResult | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.getQrCode(type);
      setCurrentQrCode(result);
      return result;
    } catch (err: any) {
      console.error('获取二维码失败:', err);
      const errorMessage = err?.err_msg || '获取二维码失败';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 刷新二维码
   * @param type 二维码类型（可选）
   * @returns Promise<QrCodeResult | null> 新的二维码信息
   */
  const refreshQrCode = async (type?: 'user_profile' | 'group_invite'): Promise<QrCodeResult | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.refreshQrCode(type);
      setCurrentQrCode(result);
      return result;
    } catch (err: any) {
      console.error('刷新二维码失败:', err);
      const errorMessage = err?.err_msg || '刷新二维码失败';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 验证二维码
   * @param qrId 二维码ID
   * @returns Promise<QrCodeVerifyResult | null> 验证结果
   */
  const verifyQrCode = async (qrId: string): Promise<QrCodeVerifyResult | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.verifyQrCode(qrId);
      return result;
    } catch (err: any) {
      console.error('二维码验证失败:', err);
      const errorMessage = err?.err_msg || '二维码验证失败';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };


  /**
   * 开始轮询验证二维码
   * @param qrId 二维码ID
   * @param onVerified 验证成功回调
   * @param onError 验证失败回调
   * @param interval 轮询间隔（毫秒，默认2000ms）
   * @param maxAttempts 最大尝试次数（默认30次）
   */
  const startVerifyPolling = (
    qrId: string,
    onVerified: (result: QrCodeVerifyResult) => void,
    onError?: (error: string) => void,
    interval: number = 2000,
    maxAttempts: number = 30
  ) => {
    if (verifyPolling) {
      console.warn('二维码验证轮询已在进行中');
      return;
    }

    setVerifyPolling(true);
    let attempts = 0;

    const poll = async () => {
      if (!verifyPolling || attempts >= maxAttempts) {
        setVerifyPolling(false);
        if (attempts >= maxAttempts) {
          const errorMsg = '二维码验证超时，请重新获取';
          setError(errorMsg);
          onError?.(errorMsg);
        }
        return;
      }

      attempts++;
      const result = await verifyQrCode(qrId);

      if (result) {
        // 验证成功
        setVerifyPolling(false);
        onVerified(result);
      } else if (verifyPolling) {
        // 继续轮询
        setTimeout(poll, interval);
      }
    };

    // 开始轮询
    poll();
  };

  /**
   * 停止验证轮询
   */
  const stopVerifyPolling = () => {
    setVerifyPolling(false);
  };

  /**
   * 清除错误信息
   */
  const clearError = () => {
    setError('');
  };

  /**
   * 清除当前二维码
   */
  const clearCurrentQrCode = () => {
    setCurrentQrCode(null);
    stopVerifyPolling();
  };

  return {
    // 状态
    isLoading,
    error,
    currentQrCode,
    verifyPolling,

    // 操作方法
    getQrCode,
    verifyQrCode,
    refreshQrCode,
    startVerifyPolling,
    stopVerifyPolling,

    // 工具方法
    clearError,
    clearCurrentQrCode
  };
}
